// locationReports.js — "only locations with reports", and "this one is new".
// ---------------------------------------------------------------------------
// v0.242.0. Brett: "Also on the droppin locations list we need a toggle to
// display only locations with reports. Maybe a notification in the portal and
// droppin to show there is a new report."
//
// THE WHOLE DIFFICULTY IS THAT A COUNT HAS THREE STATES, NOT TWO.
//   a number   — we asked, and that is the answer (0 is a real answer)
//   null       — we have not asked yet, or the asking failed
//
// The admin Locations list enriches each card with its reports AFTER the first
// paint (ProjectsScreen's parallel per-project fetch), so for a second or two
// every row's count is null. A filter that treats null as zero empties the
// screen on load and then refills it — and on a failed read it empties it for
// good, which looks exactly like "this client has no reports anywhere".
//
// So: UNKNOWN IS NEVER FILTERED AWAY. A row we could not count stays on the
// list and is counted in `unknown`, which the screen says out loud ("still
// counting 4 locations"). The filter only ever removes a location we asked
// about and got zero for.
//
// This file is pure on purpose — no React, no fetch, no storage — because the
// three-state rule is the part that is easy to get wrong and the part worth
// testing.

/**
 * How many reports does this row have? Returns a number, or null for unknown.
 *
 * Two shapes reach the Locations list and they disagree, which is itself a
 * reason to decide it in one place:
 *   • client session  — `reportsTotal` from /api/contact-projects (already
 *     filtered to what this contact may see; null when the server could not
 *     count them).
 *   • admin session   — `reports`, the array from /api/reports?projectId=,
 *     which is null until the per-card enrichment lands.
 * `reportsTotal` wins WHENEVER THE FIELD IS THERE — including when it is null.
 * That is not a detail. A client row carries `reports: []` because the client
 * card draws no per-report block, NOT because the location has no reports; a
 * fallback that read the array whenever the count was null turned "the server
 * could not count these" into "there are none" and the toggle hid the site.
 * tools/locations-toggle-check.mjs caught exactly that, in the browser, after
 * the rule above had been written down and the unit tests were green. So: the
 * presence of the key decides which source answers, and only a row that never
 * had a server count falls back to the array.
 */
export function reportsCountOf(row) {
  if (!row || typeof row !== 'object') return null;
  if ('reportsTotal' in row) {
    const t = row.reportsTotal;
    return (typeof t === 'number' && Number.isFinite(t) && t >= 0) ? Math.floor(t) : null;
  }
  if (Array.isArray(row.reports)) return row.reports.length;
  return null;
}

/** Definitely has at least one report. Unknown is NOT "yes". */
export function hasReports(row) {
  const n = reportsCountOf(row);
  return n != null && n > 0;
}

/** Definitely has none. Unknown is NOT "no" either — that is the point. */
export function hasNoReports(row) {
  const n = reportsCountOf(row);
  return n === 0;
}

/**
 * The locations list, searched and optionally narrowed to ones with reports.
 *
 * @param {Array}  rows
 * @param {object} opts { q, onlyWithReports }
 * @returns {{ rows: Array, unknown: number, hiddenNoReports: number }}
 *   rows            what to draw
 *   unknown         how many of those are still being counted (0 when the
 *                   toggle is off — nothing is pending on the user's behalf)
 *   hiddenNoReports how many the toggle removed, so the screen can say why the
 *                   list is shorter than the search alone would make it
 */
export function filterLocations(rows, opts = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const q = String(opts.q == null ? '' : opts.q).trim().toLowerCase();
  const onlyWithReports = opts.onlyWithReports === true;

  const searched = q
    ? list.filter((p) => `${(p && p.Name) || ''} ${(p && p.Address) || ''}`.toLowerCase().includes(q))
    : list.slice();

  if (!onlyWithReports) return { rows: searched, unknown: 0, hiddenNoReports: 0 };

  const kept = [];
  let unknown = 0;
  let hiddenNoReports = 0;
  for (const p of searched) {
    const n = reportsCountOf(p);
    if (n == null) { kept.push(p); unknown++; continue; }  // never hide what we did not check
    if (n > 0) { kept.push(p); continue; }
    hiddenNoReports++;
  }
  return { rows: kept, unknown, hiddenNoReports };
}

// ── "there is a new report here" ──────────────────────────────────────────
// A marker, not a mailbox. The rule is deliberately the dullest one that can
// be true: a location is NEW when its newest report is newer than the last
// time this reader opened that location. No unread table, no server state, no
// second source of truth to drift — the timestamps we already send are enough,
// and a marker that clears itself by being acted on cannot get stuck on.

/**
 * Is there a report here this reader has not opened the location since?
 *
 * @param {object} row          carries latestReportAt (ISO string or Date)
 * @param {*}      lastSeenAt   when they last opened it (ISO/ms/Date/null)
 * @returns {boolean}
 */
export function hasNewReport(row, lastSeenAt) {
  const latest = toMillis(row && row.latestReportAt);
  if (latest == null) return false;      // nothing to be new, or nothing we know
  const seen = toMillis(lastSeenAt);
  // NEVER OPENED + a report exists = new. That is the first-visit case and it
  // is the one a client most needs the marker for.
  if (seen == null) return true;
  return latest > seen;
}

/** How many of these locations are carrying a new report. */
export function countNewReports(rows, lastSeenFor) {
  const list = Array.isArray(rows) ? rows : [];
  const look = typeof lastSeenFor === 'function' ? lastSeenFor : () => null;
  let n = 0;
  for (const p of list) if (hasNewReport(p, look(p))) n++;
  return n;
}

// Anything unparseable is null (unknown) rather than 0 (the epoch), because
// 1970 compares as "older than everything" and would mark every location new.
function toMillis(v) {
  if (v == null || v === '') return null;
  if (v instanceof Date) { const t = v.getTime(); return Number.isFinite(t) ? t : null; }
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const t = new Date(String(v)).getTime();
  return Number.isFinite(t) ? t : null;
}
